import { BookOpen, Check, CircleSlash, ExternalLink, EyeOff, MapPin, Navigation, Pencil, SkipForward, Trash2, Undo2 } from 'lucide-react';
import { useState } from 'react';
import type { CalendarItem } from '@tempo/core';
import { api } from '../api';
import { MODE_LABEL, dayName, duration, time } from '../lib/format';
import { useAction, usePlaces, useSources, useTasks } from '../lib/hooks';
import { navigate } from '../lib/router';
import { EventForm } from './QuickAdd';
import { FlagBadges, ItemIcon, KIND_LABEL, ModeIcon } from './kinds';
import { Button, Field, Modal } from './ui';

/** Details + the actions that make sense for a calendar item. */
export function ItemSheet({ item, items, onClose }: { item: CalendarItem; items: CalendarItem[]; onClose: () => void }) {
  const places = usePlaces().data ?? [];
  const sources = useSources().data ?? [];
  const [editing, setEditing] = useState(false);
  const [moveTo, setMoveTo] = useState<string | null>(null);
  const place = places.find((p) => p.id === item.placeId);
  const source = sources.find((s) => s.id === item.sourceId);
  const now = Date.now();
  const started = item.start <= now;
  const target = item.targetId ? items.find((i) => i.id === item.targetId) : null;
  const travelTo = items.find((i) => i.kind === 'travel' && i.targetId === item.id);

  const done = () => onClose();
  const setStatus = useAction((status: string) => api.post(`/blocks/${item.id}/status`, { status }), (r: any) =>
    r.status === 'missed' ? 'Not done – booked again' : r.status === 'done' ? 'Nice – logged' : 'Updated',
  );
  const override = useAction((body: object) => api.put(`/routines/${item.refId}/overrides/${item.date}`, body), 'Rescheduled');
  const clearOverride = useAction(() => api.del(`/routines/${item.refId}/overrides/${item.date}`), 'Back to normal');
  const hide = useAction((hidden: boolean) => api.patch(`/events/${item.id}`, { hidden }), 'Hidden – that time is free again');
  const setPlace = useAction((placeId: string | null) => api.patch(`/events/${item.id}`, { placeId }), 'Place updated');
  const remove = useAction(() => api.del(`/events/${item.id}`), 'Deleted');
  const attend = useAction((done: boolean) => api.post('/marking', { kind: 'class', id: item.id, done }), 'Marked – thanks');
  // A School Manager study session: its item (and study guide) in School Manager.
  const tasks = useTasks(true).data ?? [];
  const task = item.kind === 'task' ? tasks.find((t) => t.id === item.refId) : undefined;
  const studyFor = task?.source === 'school-manager' ? task.externalId : null;

  if (editing && item.kind === 'event') {
    return <EventForm existing={item} onClose={onClose} />;
  }

  const act = (p: Promise<unknown>) => p.then(done).catch(() => undefined);

  return (
    <Modal
      title={
        <span className="row" style={{ gap: 10 }}>
          <span style={{ color: item.color }}>
            <ItemIcon item={item} size={20} />
          </span>
          <span className="truncate">{item.kind === 'travel' && target ? `Leave for ${target.title}` : item.title}</span>
        </span>
      }
      onClose={onClose}
    >
      <div className="stack">
        <div className="row wrap" style={{ gap: 6 }}>
          <span className="badge" style={{ background: `color-mix(in srgb, ${item.color} 18%, transparent)`, color: item.color }}>
            {item.kind === 'event' && source ? source.name : KIND_LABEL[item.kind]}
          </span>
          <FlagBadges item={item} />
        </div>
        <dl className="kv">
          <dt>When</dt>
          <dd>
            {item.allDay ? (
              `${dayName(item.start)} · all day`
            ) : item.kind === 'deadline' ? (
              `${dayName(item.start)} at ${time(item.start)}`
            ) : (
              <>
                {dayName(item.start)} · {time(item.start)}–{time(item.end)} <span className="faint">({duration(item.end - item.start)})</span>
              </>
            )}
          </dd>
          {item.kind === 'travel' ? (
            <>
              <dt>Journey</dt>
              <dd className="row" style={{ gap: 6 }}>
                <ModeIcon mode={item.mode} /> {MODE_LABEL[item.mode ?? ''] ?? 'travel'} · {places.find((p) => p.id === item.fromPlaceId)?.name ?? '?'} →{' '}
                {places.find((p) => p.id === item.toPlaceId)?.name ?? '?'}
              </dd>
              {target && (
                <>
                  <dt>Arrive for</dt>
                  <dd>
                    {target.title} at {time(target.start)}
                  </dd>
                </>
              )}
            </>
          ) : (
            (place || item.location) && (
              <>
                <dt>Where</dt>
                <dd>
                  {place?.name ?? item.location}
                  {place && item.location && item.location !== place.name ? <div className="tiny faint">{item.location}</div> : null}
                </dd>
              </>
            )
          )}
          {travelTo && (
            <>
              <dt>Leave by</dt>
              <dd>
                {time(travelTo.start)} <span className="faint">({duration(travelTo.end - travelTo.start)} {MODE_LABEL[travelTo.mode ?? ''] ?? ''})</span>
              </dd>
            </>
          )}
          {item.description && (
            <>
              <dt>Notes</dt>
              <dd style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.description}</dd>
            </>
          )}
        </dl>

        {(item.directionsUrl || travelTo?.directionsUrl) && (
          <a className="btn primary" href={item.directionsUrl ?? travelTo!.directionsUrl!} target="_blank" rel="noreferrer">
            <Navigation size={16} /> Directions
          </a>
        )}

        {item.kind === 'routine' && (
          <div className="stack tight">
            <div className="row wrap">
              {!started && (
                <Button onClick={() => act(override.mutateAsync({ skip: true }))} loading={override.isPending}>
                  <SkipForward size={16} /> Skip today
                </Button>
              )}
              {started && item.status !== 'missed' && (
                <Button onClick={() => act(setStatus.mutateAsync('missed'))} loading={setStatus.isPending}>
                  <CircleSlash size={16} /> Missed it – fit it in later
                </Button>
              )}
              {started && item.status === 'missed' && (
                <Button onClick={() => act(setStatus.mutateAsync('planned'))}>
                  <Undo2 size={16} /> Undo missed
                </Button>
              )}
              {!started && <Button onClick={() => setMoveTo(time(item.start))}>Move today…</Button>}
              {item.flags.includes('pinned') && (
                <Button variant="ghost" onClick={() => act(clearOverride.mutateAsync())}>
                  <Undo2 size={16} /> Reset today
                </Button>
              )}
            </div>
            {moveTo !== null && (
              <div className="row">
                <Field label="Start today at" className="grow">
                  <input className="input" type="time" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
                </Field>
                <Button variant="primary" style={{ alignSelf: 'flex-end' }} onClick={() => act(override.mutateAsync({ pinnedStart: moveTo }))}>
                  Move
                </Button>
              </div>
            )}
            <Button variant="ghost" onClick={() => (onClose(), navigate('/routines', { edit: item.refId }))}>
              <Pencil size={16} /> Edit routine
            </Button>
          </div>
        )}

        {item.kind === 'task' && (
          <div className="row wrap">
            {item.status !== 'done' && (
              <Button variant="primary" onClick={() => act(setStatus.mutateAsync('done'))} loading={setStatus.isPending}>
                <Check size={16} /> Done ({duration(item.end - item.start)})
              </Button>
            )}
            {item.status === 'done' && started && (
              <Button onClick={() => act(setStatus.mutateAsync('missed'))}>
                <CircleSlash size={16} /> Didn't do it – reschedule
              </Button>
            )}
            {item.status === 'planned' && started && (
              <Button onClick={() => act(setStatus.mutateAsync('missed'))}>
                <CircleSlash size={16} /> Missed – reschedule
              </Button>
            )}
            <Button variant="ghost" onClick={() => (onClose(), navigate('/tasks', { open: item.refId }))}>
              Open task
            </Button>
          </div>
        )}

        {item.kind === 'task' && studyFor && (
          <Button variant="primary" onClick={() => (onClose(), navigate('/notes', { guide: studyFor }))}>
            <BookOpen size={16} /> Go through the material
          </Button>
        )}

        {item.kind === 'event' && item.category === 'uni' && item.module && started && (
          <div className="stack tight">
            <div className="small subtle">Did you go? If not, School Manager adds a catch-up session from the notes.</div>
            <div className="row wrap">
              <Button variant="primary" onClick={() => act(attend.mutateAsync(true))} loading={attend.isPending}>
                <Check size={16} /> Went
              </Button>
              <Button onClick={() => act(attend.mutateAsync(false))} loading={attend.isPending}>
                <CircleSlash size={16} /> Didn't go – catch it up
              </Button>
            </div>
          </div>
        )}

        {item.kind === 'event' && item.editable && (
          <div className="row wrap">
            <Button onClick={() => setEditing(true)}>
              <Pencil size={16} /> Edit
            </Button>
            <Button variant="danger" onClick={() => confirm(`Delete "${item.title}"?`) && act(remove.mutateAsync())}>
              <Trash2 size={16} /> Delete
            </Button>
          </div>
        )}

        {item.kind === 'event' && !item.editable && (
          <div className="stack tight">
            <Field label="Where does this happen?" hint="Used for travel time. Synced events keep this even when the feed updates.">
              <select className="input" value={item.placeId ?? ''} onChange={(e) => setPlace.mutate(e.target.value || null)}>
                <option value="">Online / no travel</option>
                {places.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="row">
              <Button variant="ghost" onClick={() => act(hide.mutateAsync(true))}>
                <EyeOff size={16} /> Hide (I'm not going)
              </Button>
            </div>
          </div>
        )}
        {item.kind === 'deadline' && item.description?.startsWith('http') && (
          <a className="btn" href={item.description} target="_blank" rel="noreferrer">
            <ExternalLink size={16} /> Open assignment
          </a>
        )}
        {place && item.kind !== 'travel' && !travelTo && place.lat != null && (
          <a className="btn ghost" href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`} target="_blank" rel="noreferrer">
            <MapPin size={16} /> Open in Maps
          </a>
        )}
        {item.kind === 'sleep' && <p className="small subtle">Protected sleep. After a late shift Tempo moves your morning later automatically (Settings → Your day).</p>}
      </div>
    </Modal>
  );
}
